import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery, useQuery } from '@apollo/client';

import { Fade } from '@mui/material';
import { PageWrapper } from '../components/partials';
import TagsVisibilityHeader from './components/tagsVisibilityHeader/TagsVisibilityHeader';
import TutorialSteps from '../components/utilities/tutorialSteps/TutorialSteps';
import css from './page.css';
import EmptyStateIcon from '../resources/icons/creatives/empty_state.svg';
import NoDataIcon from '../resources/icons/creatives/no_data.svg';
import { CreativeFilters } from '../utils/creativesFiltersUtils';
import { useAsync, useLocalStorageState } from '../utils/customHooks';
import CreativePopup from './components/creativePopup/CreativePopup';
import { Reports } from '../services';
import { GET_DIMENSIONS_BY_CATEGORY, GET_USER_APPS, GET_USER_METRICS, GET_USER_SOURCES } from './queries';
import CreativeGalleryFilters from './components/creativeGalleryFilters/CreativeGalleryFilters';
import { getMainCreativesQueryParams, getMultiValuedDimensionsQueryParams } from './creativesQueryUtils';
import { formatCreatives, getOptionsList, hasValidMetrics } from './creativesFormattingUtils';
import CreativesLayout from './components/creativesLayout/CreativesLayout';
import {
    CardSections,
    CREATIVE_GALLERY_REPORT_TYPE,
    CREATIVES_TAB,
    DEFAULT_END_DATE,
    DEFAULT_METRICS,
    DEFAULT_START_DATE,
    GalleryContext,
    TRACK_EVENT_PREFIX,
} from './consts';
import { isObjectEmpty, trackMixpanelEvent } from '../utils/general';
import {
    ALL_METRICS_OPTIONS,
    CohortGroupsToLabel,
    createCohortOptions,
    CREATIVES_CORE_METRICS_OPTIONS,
    getAvailableMetricsOptions,
    getCohortFieldAndPeriod,
    SORTED_METRICS_OPTIONS,
} from './metricsUtils';
import { isCreativesGalleryReportRunning } from '../actions/reports';
import { useUrlParams } from '../utils/useUrlParamsHook';
import useCsvDownload from './useCsvDownload';
import fieldVisibilityRules from '../selectors/reportsConfig/fieldVisibilityRules';
import BadFilterCombination from './components/badFilterCombination/BadFilterCombination';
import CreativeExplore from '../services/creativeExplore';
import CreativeInsightsTab from './components/creativeInsights/CreativeInsightsTab';
import { MIN_DATE_RANGE_DAYS, MIXPANEL_INSIGHTS_EVENT } from './components/creativeInsights/consts';
import { getAdminModeEnabled, isCreativeInsights } from '../selectors/user';

// Local storage keys
const LOCAL_STORAGE_SELECTED_METRICS = 'creative_gallery_selected_metrics';
const LOCAL_STORAGE_SELECTED_COHORTS = 'creative_gallery_selected_cohorts';

const STEPS_AMOUNT = 3;
export const EMPTY_STATE_PARAMS = {
    icon: <EmptyStateIcon className={css.tutorialIcon} />,
    title: 'STATIC.PAGES.TAGS_VISIBILITY.EMPTY_STATE.TITLE',
    steps: [...Array(STEPS_AMOUNT)].map((_, index) => `STATIC.PAGES.TAGS_VISIBILITY.EMPTY_STATE.STEPS.${index + 1}`),
};

export const NO_DATA_PARAMS = {
    icon: <NoDataIcon className={css.tutorialIcon} />,
    title: 'STATIC.PAGES.TAGS_VISIBILITY.NO_DATA.TITLE',
    steps: [...Array(STEPS_AMOUNT)].map((_, index) => `STATIC.PAGES.TAGS_VISIBILITY.NO_DATA.STEPS.${index + 1}`),
};

const ReportsAPI = new Reports();
const CreativeExploreAPI = new CreativeExplore();

const getLoadingSections = (isLoadingDimensions, isAsyncLoading, haveCohortsChanged, hasAsyncFilters) => {
    const loadingSections = [];
    if (isLoadingDimensions && !isAsyncLoading) loadingSections.push(CardSections.MULTI_VALUED_DIMENSIONS);
    if (isAsyncLoading && !haveCohortsChanged && hasAsyncFilters) loadingSections.push(CardSections.METRICS);
    return loadingSections;
};

const TagsVisibility = () => {
    const dispatch = useDispatch();
    const showCreativeInsights = useSelector(isCreativeInsights);
    const { getParamsObject, setParamsObject, removeParam } = useUrlParams();
    const gridRef = useRef(null);
    const pageLoadingStartTime = useRef(new Date());
    const haveCohortsChanged = useRef(false);
    const tempCreatives = useRef();
    const originalMultiValuedDimensions = useRef();

    const [selectedTab, setSelectedTab] = useState(CREATIVES_TAB);
    const [isPageInitiated, setIsPageInitiated] = useState(false);
    const [insightsDownloadUrl, setInsightsDownloadUrl] = useState();

    const [dateRange, setDateRange] = useState({
        startDate: DEFAULT_START_DATE,
        endDate: DEFAULT_END_DATE,
    });

    const [app, setApp] = useState();
    const [source, setSource] = useState();
    const [selectedMetrics, setSelectedMetrics] = useState([]);
    const [viewedCreativeHash, setViewedCreativeHash] = useState();
    const [selectedCohorts, setSelectedCohorts] = useState([]);
    const [filteredCreatives, setFilteredCreatives] = useState([]);
    const [isBadFilterCombination, setIsBadFilterCombination] = useState(false);
    const [fieldsMetadata, setFieldsMetadata] = useState({});
    const [openInsightsShelf, setOpenInsightsShelf] = useState(false);

    const creativesFieldsRules = useSelector(state => field =>
        fieldVisibilityRules.creativeExplore(field, getAdminModeEnabled(state), state.user, false, false, [], false)
    );

    const [localStorageMetrics, setLocalStorageMetrics] = useLocalStorageState(LOCAL_STORAGE_SELECTED_METRICS, []);
    const [localStorageCohorts, setLocalStorageCohorts] = useLocalStorageState(LOCAL_STORAGE_SELECTED_COHORTS, []);

    const { loading: isLoadingSources, data: sourcesResponse } = useQuery(GET_USER_SOURCES, {
        variables: { reportType: CREATIVE_GALLERY_REPORT_TYPE },
    });

    const { loading: isLoadingApps, data: appsResponse } = useQuery(GET_USER_APPS);

    const { data: metricsResponse, loading: isLoadingMetrics } = useQuery(GET_USER_METRICS);

    const [{ data: eventsResponse, isLoading: isLoadingCohorts }, getCohortFields] = useAsync();

    const [getDimensionsByCategory, { data: dimensionsByCategoryResponse }] = useLazyQuery(GET_DIMENSIONS_BY_CATEGORY);

    const dimensionsByCategory = useMemo(() => JSON.parse(dimensionsByCategoryResponse?.dimensionsByCategory ?? '{}'), [
        dimensionsByCategoryResponse,
    ]);
    const apps = appsResponse?.userApps;
    const sources = sourcesResponse?.userSources;
    const appOptions = useMemo(() => getOptionsList(apps || [], 'icon'), [apps]);
    const sourceOptions = useMemo(() => getOptionsList(sources || [], 'logoPath'), [sources]);

    const metricsOptions = useMemo(() => {
        if (!metricsResponse) return [];

        const allMetrics = new Set(
            getAvailableMetricsOptions(metricsResponse.userMetrics, CREATIVES_CORE_METRICS_OPTIONS)
        );

        return SORTED_METRICS_OPTIONS.filter(({ value }) => allMetrics.has(value));
    }, [metricsResponse]);

    // The creative query is seperated for 3 queries:
    // 1. For dimensions with the unique values
    // 2. For dimensions with the multivalued values
    // We do that to avoid getting duplicate creatives (with separating the multivalued dimensions)
    // And for showing results faster since the events and periods are heavy to fetch
    const [{ data: creativesResponse, isLoading }, runCreativesFetch] = useAsync();
    const [
        { data: creativesMultiValuedDimensionsResponse, isLoading: isLoadingDimensions },
        runCreativesMultiValuedDimensionsFetch,
    ] = useAsync();
    const [{ data: creativeInsightsResponse, isLoading: isLoadingInsights }, runCreativeInsights] = useAsync();

    const specialMetrics = useMemo(
        () => metricsResponse?.userMetrics.filter(({ name }) => name.endsWith('CPE') || name.endsWith('ECVR')),
        [metricsResponse]
    );

    const cohortsOptions = useMemo(() => {
        if (!eventsResponse || !specialMetrics) return [];

        const { cohortEvents, conversionEvents, cohortMetrics, periods } = eventsResponse;

        const creativeCohortMetrics = cohortMetrics.filter(creativesFieldsRules);
        const creativesCohortEvents = cohortEvents.filter(creativesFieldsRules);
        const creativesConversionEvents = conversionEvents.filter(creativesFieldsRules);
        const creativesPeriods = periods.filter(creativesFieldsRules);

        return [
            ...createCohortOptions(creativeCohortMetrics, creativesPeriods),
            ...creativesConversionEvents.map(option => ({ value: option.name, label: option.display_name })),
            ...createCohortOptions(creativesCohortEvents, creativesPeriods),
            ...specialMetrics.map(option => ({ value: option.name, label: option.displayName })),
        ];
    }, [eventsResponse, specialMetrics]);

    const metricsToGroup = useMemo(() => {
        const mapping = {};
        if (!eventsResponse || !specialMetrics) return mapping;

        const { cohortEvents, conversionEvents, cohortMetrics } = eventsResponse;
        cohortMetrics.forEach(({ name }) => {
            mapping[name] = CohortGroupsToLabel.Metrics;
        });
        conversionEvents.forEach(({ name }) => {
            mapping[name] = CohortGroupsToLabel.ConversionEvents;
        });
        cohortEvents.forEach(({ name }) => {
            mapping[name] = CohortGroupsToLabel.CohortEvents;
        });
        specialMetrics.forEach(({ name }) => {
            mapping[name] = CohortGroupsToLabel.SpecialMetrics;
        });
        return mapping;
    }, [eventsResponse, specialMetrics]);

    const cohortsGroupBy = useCallback(
        cohortsFields =>
            cohortsFields.reduce(
                (acc, field) => {
                    const group = metricsToGroup[getCohortFieldAndPeriod(field.value)[0]];
                    acc[group].push(field);
                    return acc;
                },
                Object.values(CohortGroupsToLabel).reduce((acc, label) => ({ ...acc, [label]: [] }), {})
            ),
        [metricsToGroup]
    );

    const canQueryCreatives = !!(
        dateRange &&
        app &&
        source &&
        ((metricsOptions && selectedMetrics.length) || (cohortsOptions && selectedCohorts.length))
    );

    const creatives = useMemo(() => {
        if (!creativesResponse && tempCreatives.current)
            return tempCreatives.current.filter(creative =>
                hasValidMetrics(creative.metrics, creative.cohorts, selectedMetrics, selectedCohorts)
            );

        setFieldsMetadata(prev => creativesResponse?.metadata || prev);

        return formatCreatives(
            creativesResponse?.results,
            selectedMetrics,
            selectedCohorts,
            creativesMultiValuedDimensionsResponse?.results
        );
    }, [creativesResponse?.results, creativesMultiValuedDimensionsResponse?.results, selectedMetrics]);

    useEffect(() => {
        if (creativesResponse) haveCohortsChanged.current = false;
    }, [creativesResponse]);

    const filterToSetterMap = {
        [CreativeFilters.APP]: setApp,
        [CreativeFilters.SOURCE]: setSource,
        [CreativeFilters.METRICS]: metrics => {
            const orderedMetricsOptions = ALL_METRICS_OPTIONS.filter(metric => metrics.includes(metric.value));
            setSelectedMetrics(orderedMetricsOptions);
            setLocalStorageMetrics(metrics);
        },
        [CreativeFilters.DATE_RANGE]: setDateRange,
        [CreativeFilters.COHORTS]: cohorts => {
            // If the selected cohort updated after the first query has been launched, we need to save the creatives
            // last for now so that we can show them until the new query will return
            if (creativesResponse && creatives.length) {
                haveCohortsChanged.current = true;
                tempCreatives.current = creatives;
            }
            setSelectedCohorts(cohorts);
            setLocalStorageCohorts(cohorts.map(({ value }) => value));
        },
    };

    useEffect(() => {
        getCohortFields(ReportsAPI.getCohortDataAndConversionEvents());
    }, []);

    useEffect(() => {
        app && getDimensionsByCategory({ variables: { appId: app } });
    }, [getDimensionsByCategory, app]);

    useEffect(() => {
        dispatch(isCreativesGalleryReportRunning(isLoading));
    }, [isLoading]);

    const selectedMetricValues = useMemo(() => selectedMetrics.map(({ value }) => value), [selectedMetrics]);
    const selectedCohortValues = useMemo(() => selectedCohorts.map(({ value }) => value), [selectedCohorts]);

    const fetchCreativesData = additionalFilters => {
        if (!canQueryCreatives) return;

        setIsBadFilterCombination(false);

        const params = [dateRange, app, source, metricsOptions, selectedCohorts];

        if (!additionalFilters) {
            setParamsObject({
                app,
                source,
                metrics: selectedMetricValues,
                cohortFields: selectedCohortValues,
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
            });
        } else if (additionalFilters.length) params.push(additionalFilters);

        runCreativesFetch(
            CreativeExploreAPI.runQuery(getMainCreativesQueryParams(...params), {
                suffix: 'base',
            })
        );
        runCreativesMultiValuedDimensionsFetch(
            CreativeExploreAPI.runQuery(getMultiValuedDimensionsQueryParams(...params), {
                suffix: 'multiValued',
            })
        );
    };

    // Load creatives when multivalued filters are changed.
    const onAsyncLoad = filters => {
        if (!creatives.length) return;

        const nonEmptyFilters = filters.filter(({ values }) => values.length);

        if (!nonEmptyFilters.length) {
            originalMultiValuedDimensions.current = null;
        } else if (creativesMultiValuedDimensionsResponse && !originalMultiValuedDimensions.current) {
            originalMultiValuedDimensions.current = creativesMultiValuedDimensionsResponse.results;
        }

        // If the multivalued filter doesn't apply any druid row, we don't want to even try to query it
        // because it will return nothing, therefore we will just show the error
        if (originalMultiValuedDimensions.current && nonEmptyFilters.length) {
            const updatedCreatives = originalMultiValuedDimensions.current.filter(creative =>
                nonEmptyFilters.every(filter => filter.values.includes(creative[filter.dimension]))
            );

            if (!updatedCreatives.length) {
                !isBadFilterCombination && setIsBadFilterCombination(true);
                return;
            }
        }

        fetchCreativesData(nonEmptyFilters);
        tempCreatives.current = creatives;
    };

    useEffect(() => {
        tempCreatives.current = null;
        originalMultiValuedDimensions.current = null;
        fetchCreativesData();
    }, [dateRange, app, source]);

    // Load creatives when metrics are selected for the first time.
    useEffect(() => {
        if (!creativesResponse && !isLoading && !creativesMultiValuedDimensionsResponse && !isLoadingDimensions) {
            fetchCreativesData();
        } else if (canQueryCreatives) {
            setParamsObject({ metrics: selectedMetricValues });
        }
    }, [selectedMetricValues]);

    // Load creatives when cohort metrics are selected (lazy loading).
    useEffect(() => {
        fetchCreativesData();
    }, [selectedCohortValues]);

    useEffect(() => {
        if (showCreativeInsights && app && source && dateRange.startDate && dateRange.endDate) {
            runCreativeInsights(ReportsAPI.getCreativeInsights(app, source, dateRange.startDate, dateRange.endDate));
        }
    }, [app, source, dateRange.startDate, dateRange.endDate]);

    const selectedAppOption = appOptions.find(option => option.value === app);
    const selectedSourceOption = sourceOptions.find(option => option.value === source);

    const { onCsvDownloadClicked, isLoadingCreativesTags, creativesTags, shouldCancelTagsFetch } = useCsvDownload({
        creatives,
        selectedAppOption,
        selectedSourceOption,
        dateRange,
        selectedCohorts,
        selectedTab,
        insightsDownloadUrl,
    });

    const onFilterChange = (field, value) => {
        if ([CreativeFilters.METRICS, CreativeFilters.COHORTS].includes(field)) {
            trackMixpanelEvent(TRACK_EVENT_PREFIX, `${field} Filter Changed`, {
                app,
                source,
                selected: (field === CreativeFilters.METRICS ? selectedMetrics : selectedCohorts).map(
                    ({ label }) => label
                ),
            });
        }

        filterToSetterMap[field](value);

        if (isLoadingCreativesTags) {
            shouldCancelTagsFetch.current = true;
        }
    };

    useEffect(() => {
        if (!isPageInitiated || !pageLoadingStartTime.current) return;

        const currentTime = new Date().getMilliseconds();
        const pageLoadStartTime = pageLoadingStartTime.current.getMilliseconds();
        const loadingTime = `${currentTime - pageLoadStartTime} milliseconds`;

        trackMixpanelEvent(TRACK_EVENT_PREFIX, 'Page Loaded', {
            selectedTab,
            loadingTime,
        });
        pageLoadingStartTime.current = null;
    }, [isPageInitiated]);

    useEffect(() => {
        if (isPageInitiated) return;

        const urlParams = getParamsObject();

        if (!isObjectEmpty(urlParams)) {
            const {
                app: urlApp,
                source: urlSource,
                metrics: urlMetrics,
                startDate: urlStartDate,
                endDate: urlEndDate,
                creativeHash: urlCreativeHash,
                cohortFields: urlCohort,
                insightShelf: urlInsightShelf,
                tab: urlTab,
            } = urlParams;

            if (urlApp && !isLoadingApps) onFilterChange(CreativeFilters.APP, urlApp);
            if (urlSource && !isLoadingSources) onFilterChange(CreativeFilters.SOURCE, urlSource);
            if (urlMetrics && metricsOptions.length) onFilterChange(CreativeFilters.METRICS, urlMetrics);
            if (urlCohort && cohortsOptions?.length) {
                const urlCohortsSet = new Set(urlCohort);
                onFilterChange(
                    CreativeFilters.COHORTS,
                    cohortsOptions.filter(field => urlCohortsSet.has(field.value))
                );
            }
            if (urlStartDate && urlEndDate)
                onFilterChange(CreativeFilters.DATE_RANGE, { startDate: urlStartDate, endDate: urlEndDate });

            if (urlCreativeHash) setViewedCreativeHash(urlCreativeHash);
            if (urlInsightShelf) setOpenInsightsShelf(!!urlInsightShelf);
            if (urlTab) setSelectedTab(urlTab);
        } else if (!selectedMetrics.length && metricsOptions.length) {
            let metricsToSelect = [];
            if (localStorageMetrics.length) {
                const availableMetricsValues = new Set(metricsOptions.map(({ value }) => value));
                metricsToSelect = localStorageMetrics.filter(metric => availableMetricsValues.has(metric));
            }

            metricsToSelect = metricsToSelect.length ? metricsToSelect : DEFAULT_METRICS.map(({ value }) => value);

            onFilterChange(CreativeFilters.METRICS, metricsToSelect);
        }

        if (!isLoadingApps && !isLoadingSources && metricsOptions.length) setIsPageInitiated(true);
    }, [isLoadingApps, isLoadingSources, metricsOptions]);

    useEffect(() => {
        if (!localStorageCohorts.length || !cohortsOptions.length) return;

        const localStorageCohortsSet = new Set(localStorageCohorts);

        const availableCohorts = cohortsOptions.filter(({ value }) => localStorageCohortsSet.has(value));

        if (!availableCohorts.length) return;

        availableCohorts.sort((a, b) => localStorageCohorts.indexOf(a.value) - localStorageCohorts.indexOf(b.value));
        onFilterChange(CreativeFilters.COHORTS, availableCohorts);
    }, [cohortsOptions]);

    // Auto-select app if only one is available
    useEffect(() => {
        if (isLoadingApps || app || appOptions.length !== 1) return;

        onFilterChange(CreativeFilters.APP, appOptions[0].value);
    }, [appOptions, app, isLoadingApps]);

    useEffect(() => {
        if (!isPageInitiated) return;

        const creativeHashParam = 'creativeHash';

        if (viewedCreativeHash) {
            setParamsObject({ [creativeHashParam]: viewedCreativeHash });
        } else {
            removeParam(creativeHashParam);
        }
    }, [viewedCreativeHash]);

    useEffect(() => {
        if (!isPageInitiated) return;

        setParamsObject({ tab: selectedTab });
    }, [selectedTab]);

    const viewedCreative = viewedCreativeHash
        ? creatives.find(({ imageHash }) => imageHash === viewedCreativeHash)
        : null;

    const onSortChange = value => {
        const metricOption = ALL_METRICS_OPTIONS.find(metric => metric.value === value);
        if (!metricOption) return;
        if (selectedMetrics.some(metric => metric.value === metricOption.value)) return;

        filterToSetterMap[CreativeFilters.METRICS](
            [...selectedMetrics, metricOption].map(({ value: metricValue }) => metricValue)
        );
    };

    const isAsyncLoading = (isLoading || isLoadingDimensions) && !!tempCreatives.current;

    const displayedCreatives = filteredCreatives.filter(({ hide }) => !hide) || [];

    const onFilteredCreativesChange = updatedFilteredCreatives => {
        setFilteredCreatives(updatedFilteredCreatives);

        if (isLoadingDimensions !== false) return;

        const hasUpdatedCreatives = !!updatedFilteredCreatives.length;
        const hasPreviousCreatives = !!filteredCreatives.length;

        if (!isBadFilterCombination && !hasUpdatedCreatives) {
            setIsBadFilterCombination(true);
        } else if (isBadFilterCombination && hasUpdatedCreatives && !hasPreviousCreatives) {
            setIsBadFilterCombination(false);
        }
    };

    const handleCloseInsightsShelf = () => {
        setOpenInsightsShelf(false);
        removeParam('insightShelf');
    };

    const onExtendDateRange = () => {
        const endDate = new Date(dateRange.endDate);
        const newStartDate = new Date(endDate);
        newStartDate.setDate(endDate.getDate() - MIN_DATE_RANGE_DAYS + 1);

        setDateRange({
            startDate: newStartDate.toISOString().split('T')[0],
            endDate: dateRange.endDate,
        });
    };

    const onExpandSection = section => {
        const { startDate, endDate } = dateRange;
        trackMixpanelEvent(MIXPANEL_INSIGHTS_EVENT, 'Expanded', {
            app,
            source,
            startDate,
            endDate,
            section,
        });
    };

    return (
        <GalleryContext.Provider value={{ dimensionsByCategory, creativesTags, fieldsMetadata, showCreativeInsights }}>
            <PageWrapper className={css.pageWrapper} disablePadding>
                <TagsVisibilityHeader
                    apps={appOptions}
                    sources={sourceOptions}
                    metrics={metricsOptions}
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    app={app}
                    source={source}
                    isLoadingApps={isLoadingApps}
                    isLoadingSources={isLoadingSources}
                    onFilterChange={onFilterChange}
                    selectedMetrics={selectedMetrics}
                    creativesAmount={creatives.length}
                    onCsvDownloadClicked={onCsvDownloadClicked}
                    hasQueryBeenDone={canQueryCreatives && (!!creativesResponse || isAsyncLoading)}
                    isLoadingCsvExport={isLoadingCreativesTags}
                    isLoadingMetrics={isLoadingMetrics}
                    cohortFieldsOptions={cohortsOptions}
                    selectedCohorts={selectedCohorts}
                    isLoadingCohorts={isLoadingCohorts}
                    cohortsGroupBy={cohortsGroupBy}
                    selectedTab={selectedTab}
                    onTabChange={setSelectedTab}
                    onExtendDateRange={onExtendDateRange}
                />
                {selectedTab === CREATIVES_TAB ? (
                    <div className={css.content} onScroll={e => gridRef.current?.onScroll(e)}>
                        <CreativeGalleryFilters
                            app={app}
                            source={source}
                            creatives={creatives}
                            disabled={!canQueryCreatives || (isLoading && !isAsyncLoading) || !creatives.length}
                            onFilteredCreativesChange={onFilteredCreativesChange}
                            isLoadingMultiGeneralDimensions={isLoadingDimensions && !haveCohortsChanged.current}
                            onAsyncLoad={onAsyncLoad}
                            isBadFilterCombination={isBadFilterCombination}
                        />
                        {!canQueryCreatives ? (
                            <TutorialSteps {...EMPTY_STATE_PARAMS} />
                        ) : !creatives.length && !isLoading ? (
                            <TutorialSteps {...NO_DATA_PARAMS} />
                        ) : isBadFilterCombination ? (
                            <BadFilterCombination />
                        ) : (
                            <CreativesLayout
                                ref={gridRef}
                                isLoading={(isLoading && !isAsyncLoading) || !filteredCreatives.length}
                                loadingSections={getLoadingSections(
                                    isLoadingDimensions,
                                    isAsyncLoading,
                                    haveCohortsChanged.current,
                                    !!originalMultiValuedDimensions.current
                                )}
                                creatives={displayedCreatives}
                                onCreativeClick={setViewedCreativeHash}
                                selectedMetrics={selectedMetrics}
                                metricsOptions={metricsOptions}
                                selectedCohorts={selectedCohorts}
                                onSortChange={onSortChange}
                            />
                        )}
                    </div>
                ) : (
                    <CreativeInsightsTab
                        insights={creativeInsightsResponse}
                        isLoading={isLoadingInsights}
                        open={openInsightsShelf}
                        onClose={handleCloseInsightsShelf}
                        startDate={dateRange.startDate}
                        endDate={dateRange.endDate}
                        onExtendDateRange={onExtendDateRange}
                        onExpandSection={onExpandSection}
                        app={app}
                        source={source}
                        onInsightsDataReady={setInsightsDownloadUrl}
                    />
                )}
            </PageWrapper>
            <Fade in={!!viewedCreative}>
                <div className={css.creativePopupWrapper}>
                    {viewedCreative && (
                        <CreativePopup
                            app={selectedAppOption}
                            source={selectedSourceOption}
                            creative={viewedCreative}
                            onClose={() => setViewedCreativeHash(null)}
                        />
                    )}
                </div>
            </Fade>
        </GalleryContext.Provider>
    );
};

TagsVisibility.propTypes = {};

export default TagsVisibility;
